/**
 * Utility functions for exporting graph data and visuals in various formats.
 */

/**
 * SHA-256 of a string, hex-encoded, via the browser's native Web Crypto API
 * (no added dependency). Used to stamp exports with a checksum so a saved
 * file's contents can later be verified against what was actually generated.
 */
async function sha256Hex(str) {
  const bytes = new TextEncoder().encode(str);
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Downloads the current graph view as a PNG image.
 * @param {Object} graphRef - Reference to the graph component for rendering.
 */
export const downloadImagePNG = (graphRef, filename) => {
  if (graphRef.current) {
    const renderer = graphRef.current.renderer();
    const scene = graphRef.current.scene();
    const camera = graphRef.current.camera();

    // Render the scene to the WebGL renderer
    renderer.render(scene, camera);

    // Use the WebGL context to capture the current frame
    const gl = renderer.getContext();
    const width = gl.drawingBufferWidth;
    const height = gl.drawingBufferHeight;

    // Create a new ArrayBuffer and read pixel data into it
    const pixels = new Uint8Array(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    // Create a temporary canvas with the raw pixel data
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempContext = tempCanvas.getContext('2d');
    const imageData = new ImageData(new Uint8ClampedArray(pixels), width, height);
    tempContext.putImageData(imageData, 0, 0);

    // Create the final canvas and flip the image vertically (WebGL renders upside-down)
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    context.translate(0, canvas.height);
    context.scale(1, -1);
    context.drawImage(tempCanvas, 0, 0);

    // Convert the canvas to a PNG data URL
    const imageURL = canvas.toDataURL('image/png');

    // Create a link to trigger the download
    const link = document.createElement('a');
    link.download = filename ? String(filename) : 'e3d_graph.png';
    link.href = imageURL;
    link.click();
  }
};

export const exportGraphPngDataUrl = (graphRef) => {
  if (graphRef.current) {
    const renderer = graphRef.current.renderer();
    const scene = graphRef.current.scene();
    const camera = graphRef.current.camera();

    renderer.render(scene, camera);

    const gl = renderer.getContext();
    const width = gl.drawingBufferWidth;
    const height = gl.drawingBufferHeight;

    const pixels = new Uint8Array(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    // Create a temporary canvas with the raw pixel data
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempContext = tempCanvas.getContext('2d');
    const imageData = new ImageData(new Uint8ClampedArray(pixels), width, height);
    tempContext.putImageData(imageData, 0, 0);

    // Create the final canvas and flip the image vertically (WebGL renders upside-down)
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    context.translate(0, canvas.height);
    context.scale(1, -1);
    context.drawImage(tempCanvas, 0, 0);

    return canvas.toDataURL('image/png');
  }
  return '';
};

/**
 * Downloads the current graph view as a JPG image.
 * @param {Object} graphRef - Reference to the graph component for rendering.
 */
export const downloadImageJPG = (graphRef) => {
  if (graphRef.current) {
    const renderer = graphRef.current.renderer();
    const scene = graphRef.current.scene();
    const camera = graphRef.current.camera();

    // Render the scene
    renderer.render(scene, camera);

    // Capture the frame as a JPEG URL
    const gl = renderer.getContext();
    const width = gl.drawingBufferWidth;
    const height = gl.drawingBufferHeight;

    const pixels = new Uint8Array(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    // Create a temporary canvas with the raw pixel data
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempContext = tempCanvas.getContext('2d');
    const imageData = new ImageData(new Uint8ClampedArray(pixels), width, height);
    tempContext.putImageData(imageData, 0, 0);

    // Create the final canvas and flip the image vertically (WebGL renders upside-down)
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    context.translate(0, canvas.height);
    context.scale(1, -1);
    context.drawImage(tempCanvas, 0, 0);

    const imageURL = canvas.toDataURL('image/jpeg', 0.9);

    const link = document.createElement('a');
    link.download = 'e3d_graph.jpg';
    link.href = imageURL;
    link.click();
  }
};

/**
 * Downloads the current graph view as a PDF document.
 * @param {Object} graphRef - Reference to the graph component for rendering.
 */
export const downloadPDF = async (graphRef) => {
  if (graphRef.current) {
    const renderer = graphRef.current.renderer();
    const scene = graphRef.current.scene();
    const camera = graphRef.current.camera();

    // Render the scene
    renderer.render(scene, camera);

    // Capture the frame as a JPEG URL
    const gl = renderer.getContext();
    const width = gl.drawingBufferWidth;
    const height = gl.drawingBufferHeight;

    const pixels = new Uint8Array(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    // Create a temporary canvas with the raw pixel data
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempContext = tempCanvas.getContext('2d');
    const imageData = new ImageData(new Uint8ClampedArray(pixels), width, height);
    tempContext.putImageData(imageData, 0, 0);

    // Create the final canvas and flip the image vertically (WebGL renders upside-down)
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    context.translate(0, canvas.height);
    context.scale(1, -1);
    context.drawImage(tempCanvas, 0, 0);

    const imageURL = canvas.toDataURL('image/jpeg', 0.9);

    // Dynamically import jsPDF only when needed
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({
      orientation: width > height ? 'landscape' : 'portrait',
      unit: 'mm'
    });

    const pdfWidth = doc.internal.pageSize.getWidth();
    const pdfHeight = doc.internal.pageSize.getHeight();
    const margin = 10; // 10mm margins
    const imageWidth = pdfWidth - (margin * 2);
    const imageHeight = (height * imageWidth) / width;
    let finalImageHeight = imageHeight;
    let finalImageWidth = imageWidth;
    if (imageHeight > pdfHeight - (margin * 2)) {
      finalImageHeight = pdfHeight - (margin * 2);
      finalImageWidth = (width * finalImageHeight) / height;
    }
    const xOffset = (pdfWidth - finalImageWidth) / 2;
    const yOffset = (pdfHeight - finalImageHeight) / 2;

    doc.setFontSize(16);
    doc.text('E3D Graph', pdfWidth / 2, margin, { align: 'center' });

    doc.addImage(imageURL, 'JPEG', xOffset, yOffset, finalImageWidth, finalImageHeight);

    const generatedAt = new Date().toISOString();
    const imageHash = await sha256Hex(imageURL);
    doc.setFontSize(12);
    doc.text(`Generated: ${generatedAt}`, margin, pdfHeight - margin - 5);
    doc.setFontSize(8);
    doc.text(`SHA-256: ${imageHash}`, margin, pdfHeight - margin);

    doc.save('e3d_graph.pdf');
  }
};

/**
 * Converts graph data to CSV format.
 * @param {Object} graphData - The graph data containing nodes.
 * @param {Array} tokenList - List of tokens for additional data.
 * @returns {string} - CSV formatted string.
 */
export const graphDataToCsv = (graphData, tokenList) => {
  let csvContent = "Name,Symbol,Address,Price\n"; // CSV Header

  graphData.nodes.forEach(node => {
    const token = tokenList.find(token => token.id === node.id);
    if (token) {
      csvContent += `${node.label},${token.symbol},${node.id},${token.price}\n`;
    }
  });

  return csvContent;
};

/**
 * Downloads data as a CSV file.
 * @param {string} csvContent - The CSV content to download.
 * @param {string} fileName - The name of the file to download.
 */
export const downloadCsv = (csvContent, fileName) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Same as downloadCsv, but first appends a SHA-256 checksum + generation
 * timestamp footer covering the content above it, so the downloaded file
 * carries its own provenance record.
 */
export const downloadCsvWithHash = async (csvContent, fileName) => {
  const hash = await sha256Hex(csvContent);
  const timestamp = new Date().toISOString();
  const stamped = `${csvContent}\n# Generated,${timestamp}\n# SHA-256,${hash}\n`;
  downloadCsv(stamped, fileName);
};
